import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { ItemNoRegex } from "@coworker/apprestructured/src/scan-and-search/constants/sikConstants";
import { parseProduct } from "@coworker/app/src/services/products.service";
import { useQueries } from "@tanstack/react-query";
import { searchCombinationNameOrId } from "@coworker/app/src/components/RoomSettings/services/localCombination.service";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { CombinationProducts } from "@coworker/app/src/components/RoomSettings/types/article";
import { formatCombinationNumber } from "@coworker/apprestructured/src/scan-and-search/helpers/searchUtils";
import { useTransformSikSearch } from "@coworker/apprestructured/src/scan-and-search/hooks/search/useTransformSikSearch";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { fetchItemInfoAndPriceSR } from "@coworker/apprestructured/src/shared/services/tasksApi";

export const useSearchItems = (
  searchQuery: string,
  shouldHideSPRItems: boolean,
  shouldHideLocalCombinationItems: boolean,
  shouldSkipSikSearch: boolean = false
) => {
  const store_id = useStoreId();
  const countryId = useCountryId();
  const match = searchQuery.match(ItemNoRegex);
  const matchedQuery = match ? match.slice(1).join("") : searchQuery;
  const [itemNo] = parseProduct(matchedQuery);

  const { data, isLoading, fetchNextPage, isError } = useTransformSikSearch(
    searchQuery,
    shouldHideSPRItems,
    shouldSkipSikSearch
  );

  const shouldFetchFromOtherSources = !isLoading && data?.items?.length === 0;

  const [srItemsQuery, localCombinationQuery] = useQueries({
    queries: [
      {
        queryKey: ["item", "sr", itemNo],
        queryFn: async () => await fetchItemInfoAndPriceSR(itemNo),
        enabled: shouldFetchFromOtherSources && !!itemNo,
        staleTime: 86400000, // Since this data does not change frequently, it can stay fresh for a long time
      },
      {
        queryKey: ["local_combinations", store_id, matchedQuery],
        queryFn: async () =>
          await searchCombinationNameOrId(store_id, matchedQuery),
        enabled:
          shouldFetchFromOtherSources &&
          !shouldHideLocalCombinationItems &&
          !!matchedQuery,
        staleTime: 0,
      },
    ],
  });

  let finalItems: Item[] = [];

  if (!isLoading && !isError && data?.items && data?.items.length > 0) {
    finalItems = data.items;
  } else if (shouldFetchFromOtherSources) {
    if (
      !srItemsQuery?.isLoading &&
      !srItemsQuery?.isError &&
      srItemsQuery?.data
    ) {
      finalItems = [srItemsQuery.data];
    } else if (
      !shouldHideLocalCombinationItems &&
      !localCombinationQuery?.isLoading &&
      !localCombinationQuery?.isError &&
      localCombinationQuery?.data
    ) {
      const combinationProducts: CombinationProducts[] =
        localCombinationQuery.data;
      finalItems = combinationProducts.map(
        (filteredLocalCombinations: CombinationProducts) => ({
          name:
            filteredLocalCombinations.combinationNames.find(
              (name) => name.country_code === countryId
            )?.combination_name ?? "",
          no: filteredLocalCombinations.combinationNumber,
          type: filteredLocalCombinations.combinationType,
          noFormatted: formatCombinationNumber(
            filteredLocalCombinations.combinationNumber
          ),
          description: "",
          productTypeName: "",
          paNo: "",
          hfbNo: "",
          source: "data-service",
        })
      );
    }
  }

  return {
    data: finalItems,
    isLoading:
      isLoading || srItemsQuery?.isLoading || localCombinationQuery?.isLoading,
    isError: isError || srItemsQuery?.isError || localCombinationQuery?.isError,
    totalItems: data?.totalItems || finalItems.length,
    fetchNextPage,
  };
};
